<script setup lang="ts">
    import { onMounted, reactive } from 'vue';
    import List from '@shared/components/List/List.vue';
    import Loading from '@shared/components/Loading.vue';
    import SingleSelect from '@shared/components/SingleSelect.vue';
    import FieldsetWrapper from '@shared/components/Form/FieldsetWrapper.vue';
    import FormInput from '@shared/components/Form/FormInput.vue';
    import FormTextArea from '@shared/components/Form/FormTextArea.vue';
    import FormCheckable from '@shared/components/Form/FormCheckable.vue';
    import { SwitchStyle, type InputProps } from '@shared/components/Form/Form';
    import DateTimePicker from '@shared/components/DateTimePicker/DateTimePicker.vue';
    import CronEditor from '@shared/components/RecurringEditor/CronEditor.vue';
    import { bsAlert, AlertTypes } from '@shared/components/BsAlerts';
    import {
        getJobTypes,
        getAccountJobs,
        createJob,
        updateJob,
        type JobTypeInfo,
        type JobTaskItem,
        type CreateJobRequest,
        type UpdateJobRequest,
    } from './jobs.access';

    const props = defineProps<{ accountId: string }>();

    const state = reactive<{
        loading: boolean;
        jobTypes: JobTypeInfo[];
        jobs: JobTaskItem[];
        showCreate: boolean;
        showEdit: boolean;
        editingJob: JobTaskItem | null;
        createForm: {
            jobName: string;
            selectedType: JobTypeInfo | null;
            cronPattern: string;
            cronStart: string;
            cronTimeZone: string;
            scheduledDate: string;
            scheduledTime: string;
            isRecurring: boolean;
            data: Record<string, string>;
        };
        editForm: {
            jobName: string;
            cronPattern: string;
            cronStart: string;
            cronTimeZone: string;
            scheduledDate: string;
            scheduledTime: string;
            isRecurring: boolean;
            data: Record<string, string>;
        };
        createCronNextDates: string[];
        editCronNextDates: string[];
        saving: boolean;
    }>({
        loading: true,
        jobTypes: [],
        jobs: [],
        showCreate: false,
        showEdit: false,
        editingJob: null,
        createForm: {
            jobName: '',
            selectedType: null,
            cronPattern: '',
            cronStart: '',
            cronTimeZone: '',
            scheduledDate: '',
            scheduledTime: '',
            isRecurring: false,
            data: {},
        },
        editForm: { jobName: '', cronPattern: '', cronStart: '', cronTimeZone: '', scheduledDate: '', scheduledTime: '', isRecurring: false, data: {} },
        createCronNextDates: [],
        editCronNextDates: [],
        saving: false,
    });

    onMounted(async () => {
        const [types, jobs] = await Promise.all([getJobTypes(), getAccountJobs(props.accountId)]);
        state.jobTypes = types;
        state.jobs = jobs;
        state.loading = false;
    });

    async function refresh() {
        state.jobs = await getAccountJobs(props.accountId);
    }

    function openCreate() {
        const now = new Date();
        const date = now.toISOString().split('T')[0];
        const time = now.toTimeString().slice(0, 5);
        const isoNow = buildScheduled(date, time);
        state.createForm = {
            jobName: '',
            selectedType: null,
            cronPattern: '',
            cronStart: isoNow,
            cronTimeZone: '',
            scheduledDate: date,
            scheduledTime: time,
            isRecurring: false,
            data: {},
        };
        state.showCreate = true;
    }

    function selectType(typeName: string | null) {
        const type = typeName ? (state.jobTypes.find((t) => t.TypeName === typeName) ?? null) : null;
        state.createForm.selectedType = type;
        state.createForm.data = type ? Object.fromEntries(type.Properties.map((p) => [p.Name, ''])) : {};
    }

    function openEdit(job: JobTaskItem) {
        state.editingJob = job;
        const scheduled = job.Scheduled ? new Date(job.Scheduled) : null;
        const jobType = state.jobTypes.find((t) => t.TypeName === job.JobType);
        const data: Record<string, string> = jobType ? Object.fromEntries(jobType.Properties.map((p) => [p.Name, stringifyJobDataValue(job.JobData?.[p.Name], p.TypeName)])) : {};
        state.editForm = {
            jobName: job.JobTypeName ?? '',
            cronPattern: job.CronPattern ?? '',
            cronStart: job.Scheduled ?? '',
            cronTimeZone: job.TimeZone ?? '',
            scheduledDate: scheduled ? scheduled.toISOString().split('T')[0] : '',
            scheduledTime: scheduled ? scheduled.toTimeString().slice(0, 5) : '',
            isRecurring: job.IsRecurring,
            data,
        };
        state.showEdit = true;
    }

    function stringifyJobDataValue(value: unknown, typeName: string): string {
        if (typeName === 'Boolean' || typeName === 'Boolean?') return String(!!value);
        if (typeName === 'List<Guid>') return (Array.isArray(value) ? value : []).join('\n');
        return value == null ? '' : String(value);
    }

    function buildJobData(fields: Record<string, string>, type: JobTypeInfo): Record<string, unknown> {
        return Object.fromEntries(
            type.Properties.map((p) => {
                const raw = fields[p.Name] ?? '';
                if (p.TypeName === 'Int32' || p.TypeName === 'Int32?') return [p.Name, raw === '' ? null : parseInt(raw, 10)];
                if (p.TypeName === 'Boolean' || p.TypeName === 'Boolean?') return [p.Name, raw === 'true' || raw === '1'];
                if (p.TypeName === 'List<Guid>')
                    return [
                        p.Name,
                        raw
                            .split('\n')
                            .map((s: string) => s.trim())
                            .filter(Boolean),
                    ];
                return [p.Name, raw || null];
            })
        );
    }

    function buildScheduled(date: string, time: string): string {
        return `${date}T${time || '00:00'}:00+00:00`;
    }

    async function submitCreate() {
        if (!state.createForm.selectedType) return;
        state.saving = true;
        try {
            const request: CreateJobRequest = {
                jobName: state.createForm.jobName || null,
                jobType: state.createForm.selectedType.TypeName,
                jobData: buildJobData(state.createForm.data, state.createForm.selectedType),
                scheduled: state.createForm.isRecurring
                    ? state.createForm.cronStart
                    : buildScheduled(state.createForm.scheduledDate, state.createForm.scheduledTime),
                cronPattern: state.createForm.isRecurring ? state.createForm.cronPattern || null : null,
                isRecurring: state.createForm.isRecurring,
                timeZone: state.createForm.isRecurring ? state.createForm.cronTimeZone || null : null,
            };
            await createJob(props.accountId, request);
            state.showCreate = false;
            await refresh();
            bsAlert({ title: 'Job created', type: AlertTypes.Success });
        } finally {
            state.saving = false;
        }
    }

    async function submitEdit() {
        if (!state.editingJob) return;
        state.saving = true;
        try {
            const jobType = state.jobTypes.find((t) => t.TypeName === state.editingJob!.JobType);
            const request: UpdateJobRequest = {
                jobName: state.editForm.jobName || null,
                cronPattern: state.editForm.isRecurring ? state.editForm.cronPattern || null : null,
                scheduled: state.editForm.isRecurring
                    ? state.editForm.cronStart || null
                    : state.editForm.scheduledDate
                      ? buildScheduled(state.editForm.scheduledDate, state.editForm.scheduledTime)
                      : null,
                isRecurring: state.editForm.isRecurring,
                jobData: jobType ? buildJobData(state.editForm.data, jobType) : {},
                timeZone: state.editForm.isRecurring ? state.editForm.cronTimeZone || null : null,
            };
            await updateJob(props.accountId, state.editingJob.TaskId, request);
            state.showEdit = false;
            await refresh();
            bsAlert({ title: 'Job updated', type: AlertTypes.Success });
        } finally {
            state.saving = false;
        }
    }

    function inputTypeFor(typeName: string): InputProps['type'] {
        if (typeName === 'Int32' || typeName === 'Int32?') return 'number';
        return 'text';
    }
</script>

<template>
    <div v-show="!state.showCreate && !state.showEdit">
        <Loading :show="state.loading" loadingStyle="progress-bar" :fixed="true" />

        <template v-if="!state.loading">
            <List :items="state.jobs" itemId="TaskId" displayAs="table" :tableHeaders="['Job type', 'Status', 'Cron', 'Next run', 'Last run', '']" hideSearch>
                <template #toolbar>
                    <button class="btn btn-primary" @click="openCreate">Add job</button>
                </template>
                <template #item="{ item: job }">
                    <td>
                        {{ job.JobTypeName }}
                        <span class="mini-label mini-label-default">{{ job.IsRecurring ? 'Recurring' : 'One-time' }}</span>
                    </td>
                    <td>{{ job.Status }}</td>
                    <td>
                        <code v-if="job.CronPattern">{{ job.CronPattern }}</code>
                        <span v-else>—</span>
                    </td>
                    <td>{{ job.Scheduled ? new Date(job.Scheduled).toLocaleString() : '—' }}</td>
                    <td>{{ job.LastRun ? new Date(job.LastRun).toLocaleString() : '—' }}</td>
                    <td><button class="btn btn-default btn-xs" @click="openEdit(job)">Edit</button></td>
                </template>
            </List>
        </template>
    </div>

    <!-- CREATE  -->
    <div v-show="state.showCreate">
        <h4>Add job</h4>

        <SingleSelect
            class="form-group max-300"
            :modelValue="state.createForm.selectedType?.TypeName ?? null"
            :items="state.jobTypes"
            selectProp="TypeName"
            :displayProps="['DisplayName']"
            placeholder="Select job type..."
            label="Job type"
            @update:modelValue="selectType" />

        <template v-if="state.createForm.selectedType">
            <div class="fieldset-grid">
                <FieldsetWrapper legend="Scheduling">
                    <FormInput
                        class="form-group"
                        v-model="state.createForm.jobName"
                        label="Job name"
                        infoText="Optional — defaults to the job type name"
                        :placeholder="state.createForm.selectedType.DisplayName" />

                    <div class="form-group">
                        <FormCheckable
                            label="Recurring"
                            description="Repeat after each run"
                            :switchStyle="SwitchStyle.Standard"
                            :checked="state.createForm.isRecurring"
                            @update:modelValue="(newVal: boolean) => (state.createForm.isRecurring = newVal)" />
                    </div>

                    <template v-if="!state.createForm.isRecurring">
                        <DateTimePicker
                            class="form-group"
                            label="Scheduled date"
                            :value="state.createForm.scheduledDate"
                            @update:value="(v: string) => (state.createForm.scheduledDate = v)"
                            type="date"
                            format="yyyy-mm-dd"
                            :startEmpty="true" />

                        <DateTimePicker
                            class="form-group"
                            label="Scheduled time"
                            type="time"
                            :value_hour="state.createForm.scheduledTime ? parseInt(state.createForm.scheduledTime.split(':')[0]) : 0"
                            :value_minute="state.createForm.scheduledTime ? parseInt(state.createForm.scheduledTime.split(':')[1]) : 0"
                            @update:value_hour="
                                (h: number) =>
                                    (state.createForm.scheduledTime = `${String(h).padStart(2, '0')}:${state.createForm.scheduledTime.split(':')[1] ?? '00'}`)
                            "
                            @update:value_minute="
                                (m: number) =>
                                    (state.createForm.scheduledTime = `${state.createForm.scheduledTime.split(':')[0] ?? '00'}:${String(m).padStart(2, '0')}`)
                            " />
                    </template>
                </FieldsetWrapper>

                <template v-if="state.createForm.isRecurring">
                    <CronEditor
                        v-model:cronExpression="state.createForm.cronPattern"
                        v-model:timeZone="state.createForm.cronTimeZone"
                        v-model:selectedStart="state.createForm.cronStart"
                        v-model:nextDates="state.createCronNextDates"
                        :showExecutionHistoryButton="false" />
                </template>

                <FieldsetWrapper :legend="`Job data — ${state.createForm.selectedType.DisplayName}`">
                    <template v-for="prop in state.createForm.selectedType.Properties" :key="prop.Name">
                        <div class="form-group" v-if="prop.TypeName === 'Boolean' || prop.TypeName === 'Boolean?'">
                            <FormCheckable
                                :label="prop.Name"
                                :switchStyle="SwitchStyle.Standard"
                                :checked="!!state.createForm.data[prop.Name]"
                                @update:modelValue="(newVal: boolean) => (state.createForm.data[prop.Name] = String(newVal))" />
                        </div>

                        <FormTextArea
                            class="form-group"
                            v-else-if="prop.TypeName === 'List<Guid>'"
                            v-model="state.createForm.data[prop.Name]"
                            :label="prop.Name"
                            :infoText="prop.TypeName"
                            :rows="3"
                            placeholder="One GUID per line" />

                        <FormInput
                            class="form-group"
                            v-else
                            v-model="state.createForm.data[prop.Name]"
                            :type="inputTypeFor(prop.TypeName)"
                            :label="prop.Name"
                            :infoText="prop.TypeName"
                            :placeholder="prop.TypeName === 'Guid' || prop.TypeName === 'Guid?' ? 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' : ''" />
                    </template>
                </FieldsetWrapper>
            </div>

            <div class="form-group text-right">
                <button class="btn btn-subtle" @click="state.showCreate = false">Cancel</button>

                <button class="btn btn-primary" :disabled="state.saving" @click="submitCreate">
                    {{ state.saving ? 'Creating...' : 'Create job' }}
                </button>
            </div>
        </template>
    </div>

    <!-- EDIT -->
    <div v-show="state.showEdit">
        <h4>{{ `Edit job — ${state.editingJob?.JobTypeName ?? ''}` }}</h4>

        <div class="fieldset-grid">
            <FieldsetWrapper legend="Scheduling">
                <FormInput
                    class="form-group"
                    v-model="state.editForm.jobName"
                    label="Job name"
                    infoText="Optional — defaults to the job type name" />

                <div class="form-group">
                    <FormCheckable
                        label="Recurring"
                        description="Repeat after each run"
                        :checked="state.editForm.isRecurring"
                        :switchStyle="SwitchStyle.Standard"
                        @update:modelValue="(newVal: boolean) => (state.editForm.isRecurring = newVal)" />
                </div>

                <template v-if="!state.editForm.isRecurring">
                    <DateTimePicker
                        label="Scheduled date"
                        class="form-group"
                        :value="state.editForm.scheduledDate"
                        @update:value="(v: string) => (state.editForm.scheduledDate = v)"
                        type="date"
                        format="yyyy-mm-dd"
                        :startEmpty="true" />

                    <DateTimePicker
                        class="form-group"
                        label="Scheduled time"
                        type="time"
                        :value_hour="state.editForm.scheduledTime ? parseInt(state.editForm.scheduledTime.split(':')[0]) : 0"
                        :value_minute="state.editForm.scheduledTime ? parseInt(state.editForm.scheduledTime.split(':')[1]) : 0"
                        @update:value_hour="
                            (h: number) =>
                                (state.editForm.scheduledTime = `${String(h).padStart(2, '0')}:${state.editForm.scheduledTime.split(':')[1] ?? '00'}`)
                        "
                        @update:value_minute="
                            (m: number) =>
                                (state.editForm.scheduledTime = `${state.editForm.scheduledTime.split(':')[0] ?? '00'}:${String(m).padStart(2, '0')}`)
                        " />
                </template>
            </FieldsetWrapper>

            <template v-if="state.editForm.isRecurring">
                <CronEditor
                    v-model:cronExpression="state.editForm.cronPattern"
                    v-model:timeZone="state.editForm.cronTimeZone"
                    v-model:selectedStart="state.editForm.cronStart"
                    v-model:nextDates="state.editCronNextDates"
                    :showExecutionHistoryButton="false" />
            </template>

            <template v-if="state.editingJob">
                <FieldsetWrapper legend="Job data">
                    <template v-for="prop in state.jobTypes.find((t) => t.TypeName === state.editingJob!.JobType)?.Properties ?? []" :key="prop.Name">
                        <div class="form-group" v-if="prop.TypeName === 'Boolean' || prop.TypeName === 'Boolean?'">
                            <FormCheckable
                                :label="prop.Name"
                                :checked="!!state.editForm.data[prop.Name]"
                                @update:modelValue="(newVal: boolean) => (state.editForm.data[prop.Name] = String(newVal))" />
                        </div>

                        <FormTextArea
                            v-else-if="prop.TypeName === 'List<Guid>'"
                            class="form-group"
                            v-model="state.editForm.data[prop.Name]"
                            :label="prop.Name"
                            :infoText="prop.TypeName"
                            :rows="3"
                            placeholder="One GUID per line" />

                        <FormInput
                            class="form-group"
                            v-else
                            v-model="state.editForm.data[prop.Name]"
                            :type="inputTypeFor(prop.TypeName)"
                            :label="prop.Name"
                            :infoText="prop.TypeName"
                            :placeholder="prop.TypeName === 'Guid' || prop.TypeName === 'Guid?' ? 'xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx' : ''" />
                    </template>
                </FieldsetWrapper>
            </template>
        </div>

        <div class="form-group text-right">
            <button class="btn btn-subtle" @click="state.showEdit = false">Cancel</button>

            <button class="btn btn-primary" :disabled="state.saving" @click="submitEdit">
                {{ state.saving ? 'Saving...' : 'Save changes' }}
            </button>
        </div>
    </div>
</template>
